import { useMemo } from 'react';
import { Check, X } from 'lucide-react';

/**
 * Password Strength Indicator Component
 * Shows password strength with visual feedback and requirement checklist
 */
export default function PasswordStrengthIndicator({ password = '' }) {
  const requirements = useMemo(() => [
    { 
      label: 'At least 8 characters', 
      met: password.length >= 8,
      key: 'length'
    },
    { 
      label: 'Contains uppercase letter', 
      met: /[A-Z]/.test(password),
      key: 'uppercase'
    },
    { 
      label: 'Contains lowercase letter', 
      met: /[a-z]/.test(password),
      key: 'lowercase'
    },
    { 
      label: 'Contains a number', 
      met: /[0-9]/.test(password),
      key: 'number'
    },
    { 
      label: 'Contains special character', 
      met: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password),
      key: 'special'
    },
  ], [password]);

  const strength = useMemo(() => {
    const metCount = requirements.filter(r => r.met).length;
    
    if (password.length === 0) {
      return { level: 0, label: '', color: 'bg-slate-200' };
    }
    if (metCount <= 2) {
      return { level: 1, label: 'Weak', color: 'bg-red-500' };
    }
    if (metCount === 3) {
      return { level: 2, label: 'Fair', color: 'bg-orange-500' };
    }
    if (metCount === 4) {
      return { level: 3, label: 'Good', color: 'bg-amber-500' };
    }
    return { level: 4, label: 'Strong', color: 'bg-green-500' };
  }, [requirements, password.length]);

  if (!password) return null;

  return (
    <div className="mt-3 space-y-3" data-testid="password-strength-indicator">
      {/* Strength Bar */}
      <div className="space-y-1.5">
        <div className="flex justify-between items-center">
          <span className="text-xs text-slate-500 font-medium">Password Strength</span>
          <span className={`text-xs font-semibold ${
            strength.level === 1 ? 'text-red-600' :
            strength.level === 2 ? 'text-orange-600' :
            strength.level === 3 ? 'text-amber-600' :
            strength.level === 4 ? 'text-green-600' : 'text-slate-400'
          }`}>
            {strength.label}
          </span>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4].map((level) => (
            <div
              key={level}
              className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                level <= strength.level ? strength.color : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className="grid grid-cols-1 gap-1.5 p-3 bg-slate-50 rounded-lg border border-slate-200">
        {requirements.map((req) => (
          <div 
            key={req.key} 
            className={`flex items-center gap-2 text-xs transition-colors duration-200 ${
              req.met ? 'text-green-600' : 'text-slate-400'
            }`}
          >
            <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
              req.met ? 'bg-green-100' : 'bg-slate-100'
            }`}>
              {req.met ? (
                <Check className="w-3 h-3" />
              ) : (
                <X className="w-3 h-3" />
              )}
            </div>
            <span>{req.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Calculate password strength score (for form validation)
 * Returns: 0-4 (0 = empty, 1 = weak, 2 = fair, 3 = good, 4 = strong)
 */
export function getPasswordStrength(password) {
  if (!password) return 0;
  
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password)) score++;
  
  return Math.min(score, 4);
}

/**
 * Check if password meets minimum requirements
 * Returns true if password has at least 3 requirements met (Fair or better)
 */
export function isPasswordValid(password) {
  return getPasswordStrength(password) >= 3;
}

/**
 * Get password validation message
 */
export function getPasswordValidationMessage(password) {
  const strength = getPasswordStrength(password);
  if (strength === 0) return 'Password is required';
  if (strength === 1) return 'Password is too weak';
  if (strength === 2) return 'Password could be stronger';
  return '';
}
